import React, { useEffect, useMemo, useRef, useState } from 'react';
import config from '../config';
import {
  CATEGORY_LABELS,
  EMPTY_CATEGORIES,
  normalizeCategories,
  getActiveCategoryFilters,
  formatFabricTagLabel
} from '../utils/fabricTagCategories';
import './AdminPage.css';
import './FabricSourcesPage.css';
import FabricCardImageHover from './FabricCardImageHover';
import { buildApiUrl, fetchApiJson } from '../utils/apiUrl';

const API_BASE = config.API_BASE;
const DARK_MODE_KEY = 'fabric-sources-dark-mode';
const FABRIC_CACHE_TTL_MS = 5 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const AVAILABILITY_LABELS = {
  in_stock: 'In stock',
  sold_out: 'Sold out',
  unknown: 'Unknown'
};

function availabilityLabel(fabric) {
  if (fabric.availability_text) return fabric.availability_text;
  if (fabric.availability === 'in_stock') return 'In stock';
  if (fabric.availability === 'sold_out') return 'Sold out';
  return 'Unknown';
}

function buildCatalogParams({ search, brand, availability, selectedTags, sort }) {
  const params = new URLSearchParams();
  if (search.trim()) params.set('search', search.trim());
  if (brand) params.set('brand', brand);
  if (availability) params.set('availability', availability);
  if (selectedTags.length) params.set('tags', selectedTags.join(','));
  if (sort !== 'default') params.set('sort', sort);
  return params;
}

function readCatalogCache(cacheKey) {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || Date.now() - parsed.ts > FABRIC_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCatalogCache(cacheKey, data) {
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // ignore quota errors
  }
}

async function fetchFabricCatalog(apiBase, catalogParams) {
  try {
    return await fetchApiJson(buildApiUrl(apiBase, '/api/fabrics/catalog', catalogParams));
  } catch (catalogError) {
    const [fabricsData, tagsData] = await Promise.all([
      fetchApiJson(buildApiUrl(apiBase, '/api/fabrics', catalogParams)),
      fetchApiJson(buildApiUrl(apiBase, '/api/fabrics/tags'))
    ]);
    return {
      fabrics: fabricsData.fabrics || [],
      tags: tagsData.tags || [],
      brands: tagsData.brands || [],
      categories: tagsData.categories || {},
      availabilities: tagsData.availabilities || []
    };
  }
}

export default function FabricSourcesPage() {
  const [fabrics, setFabrics] = useState([]);
  const [filterMeta, setFilterMeta] = useState({
    tags: [],
    brands: [],
    categories: EMPTY_CATEGORIES,
    availabilities: []
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [availability, setAvailability] = useState('in_stock');
  const [categoryFilters, setCategoryFilters] = useState({
    material: 'denim',
    weight: '',
    color: '',
    attribute: '',
    other: ''
  });
  const [sort, setSort] = useState('default');
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(DARK_MODE_KEY) === 'true';
  });
  const hasLoadedOnce = useRef(false);

  const selectedTags = useMemo(() => getActiveCategoryFilters(categoryFilters), [categoryFilters]);
  const catalogParams = useMemo(
    () => buildCatalogParams({ search, brand, availability, selectedTags, sort }),
    [search, brand, availability, selectedTags, sort]
  );
  const catalogCacheKey = useMemo(
    () => `fabric-sources-catalog:${catalogParams.toString()}`,
    [catalogParams]
  );

  useEffect(() => {
    document.title = 'Fabric Sources – Bokeh';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://cdn.shopify.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    return () => {
      preconnect.remove();
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem(DARK_MODE_KEY, darkMode ? 'true' : 'false');
    document.documentElement.classList.toggle('fabric-sources-dark', darkMode);
    document.body.classList.toggle('fabric-sources-dark', darkMode);
    return () => {
      document.documentElement.classList.remove('fabric-sources-dark');
      document.body.classList.remove('fabric-sources-dark');
    };
  }, [darkMode]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      const cached = readCatalogCache(catalogCacheKey);
      if (cached) {
        setFabrics(cached.fabrics || []);
        setFilterMeta({
          tags: cached.tags || [],
          brands: cached.brands || [],
          categories: normalizeCategories(cached.categories),
          availabilities: cached.availabilities || []
        });
        setInitialLoading(false);
        setRefreshing(true);
      } else if (!hasLoadedOnce.current) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      setError('');
      try {
        const data = await fetchFabricCatalog(API_BASE, catalogParams);
        if (cancelled) return;

        const payload = {
          fabrics: data.fabrics || [],
          tags: data.tags || [],
          brands: data.brands || [],
          categories: data.categories || EMPTY_CATEGORIES,
          availabilities: data.availabilities || []
        };
        setFabrics(payload.fabrics);
        setFilterMeta({
          tags: payload.tags,
          brands: payload.brands,
          categories: normalizeCategories(payload.categories),
          availabilities: payload.availabilities
        });
        writeCatalogCache(catalogCacheKey, payload);
        hasLoadedOnce.current = true;
      } catch (err) {
        if (!cancelled && !cached) {
          setError(err.message || 'Failed to load fabrics');
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [catalogCacheKey, catalogParams]);

  function setCategoryFilter(key, value) {
    setCategoryFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setBrand('');
    setAvailability('');
    setCategoryFilters({
      material: '',
      weight: '',
      color: '',
      attribute: '',
      other: ''
    });
    setSort('default');
  }

  const categoryEntries = Object.entries(CATEGORY_LABELS).filter(
    ([key]) => (filterMeta.categories[key] || []).length > 0
  );

  return (
    <div className={`admin-page fabric-sources-page${darkMode ? ' fabric-sources-page--dark' : ''}`}>
      <header className="fabric-sources-header">
        <div className="fabric-sources-header-main">
          <h1 className="fabric-sources-title">Fabric Sources</h1>
          <div className="fabric-sources-count">
            {initialLoading ? '…' : `${fabrics.length} fabric${fabrics.length === 1 ? '' : 's'}`}
            {refreshing && !initialLoading && <span className="fabric-sources-refreshing"> · updating</span>}
          </div>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn--ghost fabric-sources-theme-btn"
          onClick={() => setDarkMode((prev) => !prev)}
          aria-pressed={darkMode}
        >
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </header>

      <section className="fabric-sources-filters">
        <div className="fabric-sources-filter-row">
          <label className="fabric-sources-label">
            Search
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="denim, indigo, cotton…"
            />
          </label>
          {filterMeta.brands.length > 0 && (
            <label className="fabric-sources-label">
              Brand
              <select value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">All</option>
                {filterMeta.brands.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>
          )}
          {filterMeta.availabilities.length > 0 && (
            <label className="fabric-sources-label">
              Availability
              <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                <option value="">All</option>
                {filterMeta.availabilities.map((value) => (
                  <option key={value} value={value}>
                    {AVAILABILITY_LABELS[value] || value}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="fabric-sources-label">
            Sort
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="default">Default</option>
              <option value="name">Name A–Z</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
            </select>
          </label>
          <button type="button" className="admin-btn admin-btn--ghost fabric-sources-clear" onClick={clearFilters}>
            Clear
          </button>
        </div>

        {categoryEntries.length > 0 && (
          <div className="fabric-sources-category-row">
            {categoryEntries.map(([key, label]) => (
              <label key={key} className="fabric-sources-label">
                {label}
                <select
                  value={categoryFilters[key]}
                  onChange={(e) => setCategoryFilter(key, e.target.value)}
                >
                  <option value="">All</option>
                  {filterMeta.categories[key].map((tag) => (
                    <option key={tag} value={tag}>{formatFabricTagLabel(tag)}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}
      </section>

      {error && <p className="fabric-sources-error">{error}</p>}

      {initialLoading && fabrics.length === 0 ? (
        <p className="fabric-sources-loading">Loading fabrics…</p>
      ) : fabrics.length === 0 ? (
        <p className="fabric-sources-empty">No fabrics match your filters.</p>
      ) : (
        <div className="fabric-sources-grid">
          {fabrics.map((fabric, fabricIndex) => (
            <article key={fabric.id} className={`fabric-card${fabric.availability === 'sold_out' ? ' fabric-card--sold-out' : ''}`}>
              <FabricCardImageHover fabric={fabric} name={fabric.name} priority={fabricIndex < 6} />
              <div className="fabric-card-body">
                <h2 className="fabric-card-name">{fabric.name}</h2>
                <p className="fabric-card-brand">{fabric.brand}</p>
                <div className="fabric-card-meta">
                  <span className="fabric-card-price">
                    {fabric.price_text || (fabric.price != null ? `$${fabric.price}` : '—')}
                  </span>
                  {fabric.weight && <span className="fabric-card-weight">{fabric.weight}</span>}
                </div>
                <p className={`fabric-card-availability fabric-card-availability--${fabric.availability || 'unknown'}`}>
                  {availabilityLabel(fabric)}
                </p>
                <a
                  href={fabric.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="admin-btn fabric-card-cta"
                >
                  Buy on {fabric.brand || fabric.source_domain} →
                </a>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
